// Dexie.js (IndexedDB wrapper) — offline storage pro mobilku.
//
// Tables:
//   pendingEvents  — events co cekaji na sync up (idempotence per UUID)
//   cachedRoute    — dnesni trasa snapshot (offline pristup)
//   cachedShift    — aktualne bezici shift (resume po restart app)
//
// Schema verze 1 — pokud rozsirim, bump version() a pridam upgrade migrate.

import Dexie, { type Table } from "dexie";
import type { RouteForToday, Shift, StopEventIn } from "@routera/shared";

export interface PendingEvent {
  /** UUID eventu — sjednoceny s payload.id pro idempotence na backendu. */
  id: string;
  shift_id: string;
  /** Cely body pro POST /mobile/shift/{id}/event nebo /mobile/events/batch. */
  payload: StopEventIn;
  /** Unix ms — kdy byl event vytvoreny na mobilce. */
  created_at: number;
  /** Pocet pokusu o sync up (pro exponential backoff). */
  attempts: number;
  /** Unix ms posledniho pokusu — backoff se meri od nej (ne od created_at). */
  last_attempt_at?: number;
  /** Posledni error message (debug). */
  last_error: string | null;
}

export interface CachedRoute {
  /** route_id z RouteForToday (primary key). */
  id: string;
  data: RouteForToday;
  /** Unix ms — kdy byla naposled fetchnuta z /mobile/route/today. */
  fetched_at: number;
}

export interface CachedShift {
  /** shift.id. */
  id: string;
  data: Shift;
  cached_at: number;
}

export interface PendingGps {
  /** UUID (auto-gen) pro idempotence + Dexie PK. */
  id: string;
  shift_id: string;
  driver_id: string;
  ts: string;         // ISO 8601 z mobilky
  lat: number;
  lng: number;
  accuracy: number | null;
  battery: number | null;
  created_at: number;
  attempts: number;
  last_attempt_at?: number;
  last_error: string | null;
}

export class RouteraDB extends Dexie {
  pendingEvents!: Table<PendingEvent, string>;
  cachedRoute!: Table<CachedRoute, string>;
  cachedShift!: Table<CachedShift, string>;
  pendingGps!: Table<PendingGps, string>;

  constructor() {
    super("routera-mobile");
    this.version(1).stores({
      pendingEvents: "id, shift_id, created_at, attempts",
      cachedRoute:   "id, fetched_at",
      cachedShift:   "id, cached_at",
    });
    // Sprint 6.1: GPS positions queue
    this.version(2).stores({
      pendingEvents: "id, shift_id, created_at, attempts",
      cachedRoute:   "id, fetched_at",
      cachedShift:   "id, cached_at",
      pendingGps:    "id, shift_id, ts, attempts",
    });
    // Fix: created_at chybel v indexu pendingGps -> orderBy("created_at") hazelo chybu
    this.version(3).stores({
      pendingGps:    "id, shift_id, ts, created_at, attempts",
    });
  }
}

export const db = new RouteraDB();

// ---------------------------------------------------------------------------
// Convenience helpers
// ---------------------------------------------------------------------------

// Po tolika neúspěšných pokusech záznam ZAHODÍME (dead-letter). Jinak by
// otrávený záznam (server ho trvale odmítá) zůstal ve frontě navždy a každý
// sync cyklus by přeposílal celý narostlý backlog → masivní únik dat.
// 25 pokusů × backoff (cap 60s) ≈ ~20 min retry, pak drop.
export const MAX_SYNC_ATTEMPTS = 25;

export async function queueEvent(shiftId: string, event: StopEventIn): Promise<void> {
  await db.pendingEvents.put({
    id: event.id,
    shift_id: shiftId,
    payload: event,
    created_at: Date.now(),
    attempts: 0,
    last_error: null,
  });
}

export async function listPendingEvents(limit = 50): Promise<PendingEvent[]> {
  return db.pendingEvents.orderBy("created_at").limit(limit).toArray();
}

export async function removePendingEvent(id: string): Promise<void> {
  await db.pendingEvents.delete(id);
}

export async function bumpAttempts(id: string, error: string | null): Promise<void> {
  const row = await db.pendingEvents.get(id);
  if (!row) return;
  if (row.attempts + 1 >= MAX_SYNC_ATTEMPTS) {
    await db.pendingEvents.delete(id);   // dead-letter — vzdáme to, ať se fronta vyčistí
    return;
  }
  await db.pendingEvents.put({
    ...row,
    attempts: row.attempts + 1,
    last_attempt_at: Date.now(),
    last_error: error,
  });
}

export async function pendingEventsCount(): Promise<number> {
  return db.pendingEvents.count();
}

// ---------------------------------------------------------------------------
// Route cache
// ---------------------------------------------------------------------------

export async function saveRoute(route: RouteForToday): Promise<void> {
  await db.cachedRoute.put({
    id: route.route_id,
    data: route,
    fetched_at: Date.now(),
  });
}

export async function loadCachedRoutes(): Promise<RouteForToday[]> {
  const rows = await db.cachedRoute.toArray();
  return rows.map((r) => r.data);
}

export async function clearCachedRoutes(): Promise<void> {
  await db.cachedRoute.clear();
}

// ---------------------------------------------------------------------------
// Shift cache
// ---------------------------------------------------------------------------

export async function saveShiftCache(shift: Shift): Promise<void> {
  await db.cachedShift.put({
    id: shift.id,
    data: shift,
    cached_at: Date.now(),
  });
}

export async function loadCachedShift(shiftId: string): Promise<Shift | null> {
  const row = await db.cachedShift.get(shiftId);
  return row ? row.data : null;
}

export async function clearShiftCache(shiftId: string): Promise<void> {
  await db.cachedShift.delete(shiftId);
}

// ---------------------------------------------------------------------------
// GPS queue (Sprint 6.1)
// ---------------------------------------------------------------------------
export async function queueGps(pos: Omit<PendingGps, "attempts" | "last_error" | "created_at">): Promise<void> {
  await db.pendingGps.put({
    ...pos,
    created_at: Date.now(),
    attempts: 0,
    last_error: null,
  });
}

export async function listPendingGps(limit = 100): Promise<PendingGps[]> {
  return db.pendingGps.orderBy("created_at").limit(limit).toArray();
}

export async function removePendingGps(id: string): Promise<void> {
  await db.pendingGps.delete(id);
}

export async function bumpGpsAttempts(id: string, error: string | null): Promise<void> {
  const row = await db.pendingGps.get(id);
  if (!row) return;
  if (row.attempts + 1 >= MAX_SYNC_ATTEMPTS) {
    await db.pendingGps.delete(id);   // dead-letter — GPS bod nestojí za věčné retry
    return;
  }
  await db.pendingGps.put({
    ...row,
    attempts: row.attempts + 1,
    last_attempt_at: Date.now(),
    last_error: error,
  });
}

export async function pendingGpsCount(): Promise<number> {
  return db.pendingGps.count();
}

// ---------------------------------------------------------------------------
// Reset při přihlášení / odhlášení (viz lib/sessionReset.ts)
// ---------------------------------------------------------------------------

/** Smaže POUZE cache (trasa + směna). Nic neodeslaného tím nezmizí — volá se
 *  při každém přihlášení i odhlášení, aby nový řidič neviděl cizí trasu. */
export async function clearOfflineCaches(): Promise<void> {
  await Promise.all([db.cachedRoute.clear(), db.cachedShift.clear()]);
}

/** Smaže NEODESLANÁ data (události + GPS body). Volat jen při ZMĚNĚ řidiče —
 *  u stejného řidiče by se tím zahodila práce, která ještě nedošla na server. */
export async function clearPendingQueues(): Promise<void> {
  await Promise.all([db.pendingEvents.clear(), db.pendingGps.clear()]);
}
